import { useRuntimeConfig } from '#imports'
import type { RouteRecordRaw } from 'vue-router'

import { ROUTE_NAMES } from '~/constants/routes'

import { PUBLICATION_STATE } from './Listings.constants'
import translations from './pages/index/Index.translations'

function onboardingRoutes(): RouteRecordRaw[] {
  return [
    {
      component: () => import('~/scopes/listings/pages/index/Index.vue'),
      redirect: {
        name: ROUTE_NAMES.ONBOARDING.LISTINGS.TABS.ACTIVE,
      },
      path: '/bo-seller/onboarding/listings',
      name: ROUTE_NAMES.ONBOARDING.LISTINGS.INDEX,
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
      children: [
        {
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGS.TABS.ACTIVE,
          path: 'active',
          meta: {
            publicationState: PUBLICATION_STATE.ONLINE,
            tabItem: {
              label: translations.tabActive,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGS.TABS.MISSING_INFOS,
          path: 'missing-infos',
          meta: {
            publicationState: PUBLICATION_STATE.MISSING_PRICE_OR_COMMENT,
            tabItem: {
              label: translations.tabMissingInfos,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGS.TABS.IN_VALIDATION,
          path: 'in-validation',
          meta: {
            publicationState: PUBLICATION_STATE.IN_VALIDATION,
            tabItem: {
              label: translations.tabInValidation,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGS.TABS.ARCHIVED,
          path: 'archived',
          meta: {
            publicationState: PUBLICATION_STATE.DISABLED,
            tabItem: {
              label: translations.tabArchived,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
      ],
    },
    {
      path: '/bo-seller/onboarding/listings/product/create',
      name: ROUTE_NAMES.ONBOARDING.LISTINGS.CREATE.PRODUCT.CREATE,
      component: () =>
        import('~/scopes/listings/pages/product-create/ProductCreate.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
    {
      path: '/bo-seller/onboarding/listings/product/search',
      name: ROUTE_NAMES.ONBOARDING.LISTINGS.CREATE.PRODUCT.SEARCH,
      component: () =>
        import('~/scopes/listings/pages/product-search/ProductSearch.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
    {
      path: '/bo-seller/onboarding/listings/create/:productId',
      name: ROUTE_NAMES.ONBOARDING.LISTINGS.CREATE.DETAILS,
      component: () => import('~/scopes/listings/pages/create/Create.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
    {
      redirect: {
        name: ROUTE_NAMES.BADOOM.LISTINGS.PRODUCT.DUPLICATE,
      },
      name: ROUTE_NAMES.ONBOARDING.LISTINGS.CREATE.PRODUCT.DUPLICATE,
      path: '/bo-seller/onboarding/duplicate_product/:productId',
    },
    // LISTINGSV2 onboarding routes
    {
      component: () => import('~/scopes/listings/pages/listingsV2/Index.vue'),
      redirect: {
        name: ROUTE_NAMES.ONBOARDING.LISTINGSV2.TABS.ACTIVE,
      },
      path: '/bo-seller/onboarding/listings-new',
      name: ROUTE_NAMES.ONBOARDING.LISTINGSV2.INDEX,
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
      children: [
        {
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGSV2.TABS.ACTIVE,
          path: 'active',
          meta: {
            publicationState: PUBLICATION_STATE.ONLINE,
            tabItem: {
              label: translations.tabActive,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGSV2.TABS.IN_VALIDATION,
          path: 'in-validation',
          meta: {
            publicationState: PUBLICATION_STATE.IN_VALIDATION,
            tabItem: {
              label: translations.tabInValidation,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
        {
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          name: ROUTE_NAMES.ONBOARDING.LISTINGSV2.TABS.ARCHIVED,
          path: 'archived',
          meta: {
            publicationState: PUBLICATION_STATE.DISABLED,
            tabItem: {
              label: translations.tabArchived,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
        },
      ],
    },
  ]
}

const routes = (): RouteRecordRaw[] => {
  const { FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2 } = useRuntimeConfig().public

  return [
    ...onboardingRoutes(),
    {
      path: '/bo-seller/listings',
      name: ROUTE_NAMES.LISTINGS.INDEX,
      component: () => import('~/scopes/listings/pages/index/Index.vue'),
      redirect: { name: ROUTE_NAMES.LISTINGS.TABS.ACTIVE },
      meta: {
        autonomousBannerPageName: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
          ? 'listings-temp'
          : 'listings',
        owners: ['bot-squad-seller-experience-front'],
      },
      children: [
        {
          path: 'active',
          name: ROUTE_NAMES.LISTINGS.TABS.ACTIVE,
          meta: {
            publicationState: PUBLICATION_STATE.ONLINE,
            autonomousBannerPageName: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
              ? 'listings-temp.active'
              : 'listings.active',
            tabItem: {
              label: translations.tabActive,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
        },
        {
          path: 'missing-infos',
          name: ROUTE_NAMES.LISTINGS.TABS.MISSING_INFOS,
          meta: {
            publicationState: PUBLICATION_STATE.MISSING_PRICE_OR_COMMENT,
            autonomousBannerPageName: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
              ? 'listings-temp.active'
              : 'listings.missing-infos',
            tabItem: {
              label: translations.tabMissingInfos,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
        },
        {
          path: 'in-validation',
          name: ROUTE_NAMES.LISTINGS.TABS.IN_VALIDATION,
          meta: {
            publicationState: PUBLICATION_STATE.IN_VALIDATION,
            autonomousBannerPageName: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
              ? 'listings-temp.active'
              : 'listings.in-validation',
            tabItem: {
              label: translations.tabInValidation,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
        },
        {
          path: 'archived',
          name: ROUTE_NAMES.LISTINGS.TABS.ARCHIVED,
          meta: {
            publicationState: PUBLICATION_STATE.DISABLED,
            autonomousBannerPageName: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
              ? 'listings-temp.active'
              : 'listings.archived',
            tabItem: {
              label: translations.tabArchived,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/index/components/Tab/Tab.vue'),
        },
      ],
    },
    {
      path: '/bo-seller/listings-new',
      name: ROUTE_NAMES.LISTINGSV2.INDEX,
      component: () =>
        FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
          ? import('~/scopes/listings/pages/listingsV2/Index.vue')
          : import('~/scopes/listings/pages/index/Index.vue'),
      redirect: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
        ? { name: ROUTE_NAMES.LISTINGSV2.TABS.ACTIVE }
        : { name: ROUTE_NAMES.LISTINGS.TABS.ACTIVE },
      meta: {
        autonomousBannerPageName: 'listings-new',
        owners: ['bot-squad-seller-experience-front'],
      },
      children: [
        {
          path: 'active',
          name: ROUTE_NAMES.LISTINGSV2.TABS.ACTIVE,
          meta: {
            publicationState: PUBLICATION_STATE.ONLINE,
            tabItem: {
              label: translations.tabActive,
            },
            autonomousBannerPageName: 'listings-new.active',
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          redirect: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
            ? undefined
            : { name: ROUTE_NAMES.LISTINGS.TABS.ACTIVE },
          children: [],
        },
        {
          path: 'in-validation',
          name: ROUTE_NAMES.LISTINGSV2.TABS.IN_VALIDATION,
          meta: {
            publicationState: PUBLICATION_STATE.IN_VALIDATION,
            tabItem: {
              label: translations.tabInValidation,
            },
            autonomousBannerPageName: 'listings-new.active',
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          redirect: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
            ? undefined
            : { name: ROUTE_NAMES.LISTINGS.TABS.IN_VALIDATION },
          children: [],
        },
        {
          path: 'archived',
          name: ROUTE_NAMES.LISTINGSV2.TABS.ARCHIVED,
          meta: {
            autonomousBannerPageName: 'listings-new.active',
            publicationState: PUBLICATION_STATE.DISABLED,
            tabItem: {
              label: translations.tabArchived,
            },
            owners: ['bot-squad-seller-experience-front'],
          },
          component: () =>
            import('~/scopes/listings/pages/listingsV2/components/Tab/Tab.vue'),
          redirect: FF_BO_SELLER_ENABLE_LISTINGS_PAGE_V2
            ? undefined
            : { name: ROUTE_NAMES.LISTINGS.TABS.ARCHIVED },
          children: [],
        },
      ],
    },

    {
      path: '/bo-seller/listings/product/search',
      name: ROUTE_NAMES.LISTINGS.PRODUCT.SEARCH,
      component: () =>
        import('~/scopes/listings/pages/product-search/ProductSearch.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
    {
      path: '/bo-seller/listings/create/:productId',
      name: ROUTE_NAMES.LISTINGS.CREATE,
      component: () => import('~/scopes/listings/pages/create/Create.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
    {
      path: '/bo-seller/listings/product/create',
      name: ROUTE_NAMES.LISTINGS.PRODUCT.CREATE,
      component: () =>
        import('~/scopes/listings/pages/product-create/ProductCreate.vue'),
      meta: {
        owners: ['bot-squad-seller-experience-front'],
      },
    },
  ]
}

export default routes
